import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface IReinstateDialog {
	unitLabel: string;
	/** v1 servers only have DELETE: the reason cannot be stored, and the dialog says so. */
	reasonStored: boolean;
	pending: boolean;
	error: string | null;
	onCancel: () => void;
	onConfirm: (reason: string) => void;
}

/**
 * Reinstating puts a unit back into every count and expected-units denominator, so it asks for
 * a reason (stored on the row's audit trail) — never `window.confirm`.
 */
const ReinstateDialog = ({ unitLabel, reasonStored, pending, error, onCancel, onConfirm }: IReinstateDialog) => {
	const [reason, setReason] = useState('');
	const [touched, setTouched] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);
	const titleId = useId();
	const hintId = useId();
	const valid = reason.trim().length > 0;

	const onCancelRef = useRef(onCancel);
	onCancelRef.current = onCancel;

	useEffect(() => {
		const opener = document.activeElement as HTMLElement | null;
		inputRef.current?.focus();
		// Capture phase: Escape closes the dialog only, not the right panel underneath.
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				onCancelRef.current();
			}
		};
		document.addEventListener('keydown', onKey, true);
		return () => {
			document.removeEventListener('keydown', onKey, true);
			opener?.focus?.();
		};
	}, []);

	return createPortal(
		<div
			className='pc-dialog-backdrop'
			onClick={onCancel}
		>
			<div
				className='pc-dialog'
				role='dialog'
				aria-modal='true'
				aria-labelledby={titleId}
				onClick={(e) => e.stopPropagation()}
			>
				<h2
					id={titleId}
					className='pc-dialog__title'
				>
					Reinstate {unitLabel}?
				</h2>
				<p className='pc-dialog__body'>
					The unit goes back into your expected units and every count that leaves cancelled units out. Use this when INEC reversed
					the cancellation or the report was wrong.
				</p>
				<form
					onSubmit={(e) => {
						e.preventDefault();
						setTouched(true);
						if (valid && !pending) onConfirm(reason);
					}}
				>
					<label className='pc-field'>
						<span>Reason (required)</span>
						<textarea
							ref={inputRef}
							className='form-control form-control-sm'
							rows={3}
							maxLength={500}
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							aria-invalid={touched && !valid}
							aria-describedby={valid ? undefined : hintId}
							onBlur={() => setTouched(true)}
						/>
					</label>
					{!valid ? (
						<p
							id={hintId}
							className={touched ? 'pc-error' : 'pc-muted'}
							role={touched ? 'alert' : undefined}
						>
							Add a reason to reinstate this unit.
						</p>
					) : null}
					{!reasonStored ? (
						<p className='pc-muted'>
							This server records the reinstatement but not the reason. Note it somewhere your team will see it.
						</p>
					) : null}
					{error ? (
						<p
							className='pc-error'
							role='alert'
						>
							{error}
						</p>
					) : null}
					<div className='pc-dialog__actions'>
						<button
							type='button'
							className='pc-btn'
							onClick={onCancel}
						>
							Keep cancelled
						</button>
						<button
							type='submit'
							className='pc-btn pc-btn--danger'
							// The reason is required: the button stays off until there is one, and the hint
							// above says so — never a silent no-op.
							disabled={pending || !valid}
						>
							{pending ? 'Reinstating…' : 'Reinstate unit'}
						</button>
					</div>
				</form>
			</div>
		</div>,
		document.body
	);
};

export default ReinstateDialog;
